import type { CSSProperties } from 'react';

/**
 * A labelled track whose thumb stops only at the given options, every one of
 * them marked — for picking one value from an ordered set. The screen owns the
 * value and formats the labels; this only reports the next one. Built on the
 * native range input over option indices, so drag, tap, keys and assistive
 * technology come from the browser.
 */
export function Slider<T>({
  id,
  label,
  options,
  value,
  onChange,
}: {
  /** Ties the visible label to the input, as in `Field`. */
  id: string;
  label: string;
  options: readonly { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}) {
  const current = options.findIndex((option) => option.value === value);
  const last = options.length - 1;
  // A stale value parks the thumb on the first option, still one way in.
  const index = Math.max(current, 0);
  const at = (i: number) => (last > 0 ? i / last : 0);

  return (
    <div
      className="slider"
      style={{ '--slider-fill': at(index) } as CSSProperties}
    >
      <div className="slider-head">
        <label htmlFor={id} className="field-label">
          {label}
        </label>
        {/* The input announces it through aria-valuetext. */}
        <span className="slider-value" aria-hidden="true">
          {options[current]?.label}
        </span>
      </div>
      <input
        id={id}
        type="range"
        className="slider-input"
        min={0}
        max={last}
        step={1}
        value={index}
        aria-valuetext={options[current]?.label}
        onChange={(e) => onChange(options[Number(e.target.value)].value)}
      />
      <div className="slider-marks" aria-hidden="true">
        {options.map((option, i) => (
          <span
            key={i}
            className={i === current ? 'slider-mark is-active' : 'slider-mark'}
            style={{ left: `${at(i) * 100}%` }}
          >
            <span className="slider-mark-label">{option.label}</span>
          </span>
        ))}
      </div>
    </div>
  );
}
